import React, {ReactElement, ReactNode} from "react";
import classNames from "classnames/bind";

import styles from "./modal.module.scss";
import {Body} from "../typography";
import {Subheader} from "../typography/Subheader";
import {CloseMediumIcon} from "../icons";

const bStyles = classNames.bind(styles);

export interface BaseModalProps {
	isOpen: boolean;
	onClose: (event?: React.MouseEvent) => void;
	onClick?: (event?: React.MouseEvent) => void;
}

export interface ModalProps extends BaseModalProps {
	title?: string | ReactElement;
	portal?: boolean;
	description?: string | ReactElement;
	children: ReactNode;
	className?: string;
	type?: "centered" | "sidebar";
	size?: "x-small" | "small" | "medium" | "large" | "mask";
	headerClassName?: string;
	descriptionClassName?: string;
	headerWrapperClassName?: string;
	backgroundColor?: string;
	"aria-label"?: string;
}

export const Modal = ({
	title = "",
	portal = false,
	description,
	children,
	isOpen,
	onClose,
	className,
	onClick,
	type = "centered",
	size,
	headerClassName,
	backgroundColor,
	headerWrapperClassName,
	descriptionClassName,
	...rest
}: ModalProps): ReactElement | null => {
	const content = (
		<div className={bStyles("wrapper", type, {transparent: backgroundColor === "transparent"})} {...rest}>
			<div className={bStyles("modal", className, type, size)} onClick={onClick}>
				<CloseMediumIcon onClick={onClose} className={bStyles("closeIcon")} />
				<div className={bStyles("header", headerWrapperClassName)}>
					<Subheader size="l" type="semibold" color="text-body" className={headerClassName}>
						{title}
					</Subheader>
					{description && (
						<Body color="text-secondary" className={descriptionClassName}>
							{description}
						</Body>
					)}
				</div>
				{children}
			</div>

			<div className={bStyles("backdrop")} onClick={onClose} />
		</div>
	);

	if (!isOpen) return null;

	return content;
};
